import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchWithCorpId } from '../utils/api';

const SortIcon = ({ col, sortConfig }) => {
  if (sortConfig.key !== col) return <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ opacity: 0.35, marginLeft: 4 }}><path d="M12 5v14M5 12l7-7 7 7"/></svg>;
  return sortConfig.direction === 'asc'
    ? <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" strokeWidth="2.5" style={{ marginLeft: 4 }}><path d="M12 19V5M5 12l7-7 7 7"/></svg>
    : <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" strokeWidth="2.5" style={{ marginLeft: 4 }}><path d="M12 5v14M5 12l7 7 7-7"/></svg>;
};

function AdminTable({ onEdit }) {
  const navigate = useNavigate();
  const [admins, setAdmins] = useState([]);
  const [filteredAdmins, setFilteredAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState({ category: '', feature: '', description: '', value: '' });
  const [sortConfig, setSortConfig] = useState({ key: '', direction: 'asc' });
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(10);
  const [totalElements, setTotalElements] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  const fetchAdmins = async () => {
    try {
      const data = await fetchWithCorpId(`/admin?page=${page}&size=${size}`);
      if (Array.isArray(data)) {
        setAdmins(data); setFilteredAdmins(data); setTotalElements(data.length); setTotalPages(1);
      } else {
        const { content = [], totalElements: total = 0, totalPages: totalP = 0 } = data || {};
        setAdmins(content); setFilteredAdmins(content);
        setTotalElements(total); setTotalPages(totalP || (content.length ? 1 : 0));
      }
    } catch { setError('Failed to load admin settings'); setAdmins([]); setFilteredAdmins([]);
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchAdmins(); }, [page, size]);
  useEffect(() => { applyFilters(); }, [filter, admins]);
  useEffect(() => { applySorting(); }, [sortConfig]);

  const applyFilters = () => {
    if (!Array.isArray(admins)) { setFilteredAdmins([]); return; }
    setFilteredAdmins(admins.filter(a =>
      (!filter.category || a.category?.toLowerCase().includes(filter.category.toLowerCase())) &&
      (!filter.feature || a.feature?.toLowerCase().includes(filter.feature.toLowerCase())) &&
      (!filter.description || a.description?.toLowerCase().includes(filter.description.toLowerCase())) &&
      (!filter.value || a.value?.toLowerCase().includes(filter.value.toLowerCase()))
    ));
  };

  const applySorting = () => {
    if (!sortConfig.key || !Array.isArray(filteredAdmins)) return;
    setFilteredAdmins(prev => [...prev].sort((a, b) => {
      const vA = a[sortConfig.key], vB = b[sortConfig.key];
      if (vA == null) return 1; if (vB == null) return -1;
      if (typeof vA === 'number') return sortConfig.direction === 'asc' ? vA - vB : vB - vA;
      return sortConfig.direction === 'asc' ? vA.toString().localeCompare(vB.toString()) : vB.toString().localeCompare(vA.toString());
    }));
  };

  const handleSort = (key) => setSortConfig(p => ({ key, direction: p.key === key && p.direction === 'asc' ? 'desc' : 'asc' }));
  const handleFilterChange = (e) => setFilter(p => ({ ...p, [e.target.name]: e.target.value }));
  const handleChangePageSize = (e) => { setSize(parseInt(e.target.value, 10)); setPage(0); };

  return (
    <div className="page-container">
      <div className="table-toolbar">
        <div>
          <h1 style={{ margin: 0, fontSize: '22px', fontWeight: '800', color: '#1e293b' }}>Admin Settings</h1>
          <p style={{ margin: '2px 0 0', fontSize: '13px', color: '#64748b' }}>System configuration and feature settings</p>
        </div>
        <button className="btn-primary" onClick={() => navigate('/on/admin/new')}>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ marginRight: 6 }}><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
          New Setting
        </button>
      </div>

      <div className="filter-bar">
        <input name="category" placeholder="Category" value={filter.category} onChange={handleFilterChange} />
        <input name="feature" placeholder="Feature" value={filter.feature} onChange={handleFilterChange} />
        <input name="description" placeholder="Description" value={filter.description} onChange={handleFilterChange} />
        <input name="value" placeholder="Value" value={filter.value} onChange={handleFilterChange} />
        <button className="btn-secondary" onClick={() => setFilter({ category: '', feature: '', description: '', value: '' })}>Clear</button>
      </div>

      {loading ? (
        <div style={{ padding: '48px', textAlign: 'center', color: '#94a3b8', fontSize: '14px' }}>Loading settings…</div>
      ) : error ? (
        <div className="alert error">{error}</div>
      ) : (
        <div className="table-wrapper">
          <table className="data-table">
            <thead>
              <tr>
                <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('feature')}>Feature <SortIcon col="feature" sortConfig={sortConfig} /></th>
                <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('category')}>Category <SortIcon col="category" sortConfig={sortConfig} /></th>
                <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('description')}>Description <SortIcon col="description" sortConfig={sortConfig} /></th>
                <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('value')}>Value <SortIcon col="value" sortConfig={sortConfig} /></th>
                <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('updateDate')}>Last Updated <SortIcon col="updateDate" sortConfig={sortConfig} /></th>
              </tr>
            </thead>
            <tbody>
              {filteredAdmins.length === 0 ? (
                <tr className="state-row"><td colSpan="5">No admin settings found.</td></tr>
              ) : filteredAdmins.map(admin => (
                <tr key={admin.id}>
                  <td><span className="table-link" onClick={() => onEdit(admin)}>{admin.feature}</span></td>
                  <td>{admin.category}</td>
                  <td style={{ color: '#64748b', maxWidth: '260px' }}>{admin.description}</td>
                  <td><code style={{ background: '#f1f5f9', padding: '2px 7px', borderRadius: '5px', fontSize: '12.5px', color: '#1e293b' }}>{admin.value}</code></td>
                  <td style={{ color: '#94a3b8', fontSize: '13px' }}>{admin.updateDate}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="pagination">
            <span style={{ fontSize: '13px', color: '#64748b', marginRight: 'auto' }}>
              {typeof totalElements === 'number' ? `${totalElements} total` : ''}
            </span>
            <label style={{ fontSize: '13px', color: '#64748b' }}>Rows:&nbsp;
              <select value={size} onChange={handleChangePageSize} style={{ fontSize: '13px', border: '1px solid #e2e8f0', borderRadius: '6px', padding: '3px 6px' }}>
                {[5, 10, 25, 50, 100].map(o => <option key={o} value={o}>{o}</option>)}
              </select>
            </label>
            <span style={{ fontSize: '13px', color: '#64748b' }}>Page {totalPages > 0 ? page + 1 : 0} of {totalPages}</span>
            <button onClick={() => setPage(p => Math.max(p - 1, 0))} disabled={page === 0}>← Prev</button>
            <button onClick={() => setPage(p => Math.min(p + 1, totalPages - 1))} disabled={totalPages === 0 || page >= totalPages - 1}>Next →</button>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminTable;
